/**
 * 页面头部组件
 * 包含面包屑导航和页面标题
 * Requirements: 7.1, 7.4, 7.5
 */

import { Breadcrumb, type BreadcrumbItem } from '@/components/common/breadcrumb'
import { useBreadcrumb, type BreadcrumbContext } from '@/hooks'
import { cn } from '@/lib/utils'

export interface PageHeaderProps {
  /** 页面标题 */
  title?: string
  /** 页面描述 */
  description?: string
  /** 面包屑上下文（用于动态标题） */
  breadcrumbContext?: BreadcrumbContext
  /** 自定义面包屑项（覆盖自动生成） */
  breadcrumbItems?: BreadcrumbItem[]
  /** 是否显示面包屑 */
  showBreadcrumb?: boolean
  /** 右侧操作区域 */
  actions?: React.ReactNode
  /** 自定义类名 */
  className?: string
  /** 子元素（标题下方内容） */
  children?: React.ReactNode
}

/**
 * 页面头部组件
 * 自动生成面包屑导航，支持自定义标题和操作按钮
 */
export function PageHeader({
  title,
  description,
  breadcrumbContext,
  breadcrumbItems,
  showBreadcrumb = true,
  actions,
  className,
  children,
}: PageHeaderProps) {
  // 自动生成面包屑
  const autoBreadcrumbItems = useBreadcrumb(breadcrumbContext)
  const items = breadcrumbItems ?? autoBreadcrumbItems

  return (
    <div className={cn('space-y-4', className)}>
      {/* 面包屑导航 */}
      {showBreadcrumb && items.length > 0 && (
        <Breadcrumb items={items} maxItems={4} />
      )}

      {/* 标题和操作区域 */}
      {(title || actions) && (
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            {title && (
              <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
            )}
            {description && (
              <p className="text-muted-foreground">{description}</p>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}

      {/* 子内容 */}
      {children}
    </div>
  )
}

/**
 * 简化的页面头部组件
 * 仅显示面包屑，不显示标题
 */
export function PageBreadcrumb({
  context,
  items,
  className,
}: {
  context?: BreadcrumbContext
  items?: BreadcrumbItem[]
  className?: string
}) {
  const autoBreadcrumbItems = useBreadcrumb(context)
  const breadcrumbItems = items ?? autoBreadcrumbItems

  if (breadcrumbItems.length === 0) {
    return null
  }

  return <Breadcrumb items={breadcrumbItems} maxItems={4} className={className} />
}
